import React, { FunctionComponent } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { Collapsible, Icon, IconColor } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { bemClassNames } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { IAppState } from "@multiweb/types/react";
import { createUpdateGeneralCollapsibleState } from "../../../actions";
import { LOC_BASIC_CONDITION, LOC_CONTAINS_ACTIONS, LOC_INHERIT_PARTICIPANTS_FROM_PREVIOUS_ACTIVITY, LOC_KILL_CHILDREN, LOC_LEGEND, LOC_LEGEND_BASIC_CONDITION_DESCRIPTION, LOC_LEGEND_CONTAINS_ACTIONS_DESCRIPTION, LOC_LEGEND_CONTAINS_BASIC_CONDITION_DESCRIPTION, LOC_LEGEND_INHERIT_PARTICIPANTS_DESCRIPTION, LOC_LEGEND_KILL_CHILDREN_DESCRIPTION, LOC_LEGEND_ONGOING_DESCRIPTION, LOC_LEGEND_ONLINE_EVAL_CONDITION_DESCRIPTION, LOC_LEGEND_PERTIENCE_DESCRIPTION, LOC_LEGEND_TYPE_FOR_JOIN_DESCRIPTION, LOC_ONGOING_ACTIVITY, LOC_ONLINE_EVAL_CONDITION, LOC_PERTINENCE_TO_SETS, LOC_TYPE_FOR_JOIN } from "../../../locKeys";
import { getGeneralPanelStateContainer } from "../../../selectors";
import { WorkflowEditorProps } from "../../../types";
import { LegendItem } from "../components/LegendItem";

const transitionClassNames = bemClassNames("wf-transition");

type LegendContainerProps = {
    readonly isOpen: boolean;
    readonly updateLegendPanelState: (isExpanded: boolean) => void;
    readonly legendText: string;
};

const LegendContainerComponent: FunctionComponent<LegendContainerProps> = ({ isOpen, legendText, updateLegendPanelState }) => (
    <Collapsible qa="legend-collapsible-section" captionText={legendText} defaultOpened={isOpen} onExpansionChange={updateLegendPanelState} handleExpansionExternally>
        <LegendItem
            legendItemIcon={<Icon iconCode={IconCode.docPaperStack} color={IconColor.orange} />}
            legendItemHeaderLocKey={LOC_PERTINENCE_TO_SETS}
            legendItemDescriptionLocKey={LOC_LEGEND_PERTIENCE_DESCRIPTION} />
        <LegendItem
            legendItemIcon={<Icon iconCode={IconCode.uiCloseParallelConnection}/>}
            legendItemHeaderLocKey={LOC_KILL_CHILDREN}
            legendItemDescriptionLocKey={LOC_LEGEND_KILL_CHILDREN_DESCRIPTION} />
        <LegendItem
            legendItemIcon={<Icon iconCode={IconCode.uiArrowRight} />}
            legendItemHeaderLocKey={LOC_ONGOING_ACTIVITY}
            legendItemDescriptionLocKey={LOC_LEGEND_ONGOING_DESCRIPTION} />
        <LegendItem
            legendItemIcon={<Icon iconCode={IconCode.userGroup} />}
            legendItemHeaderLocKey={LOC_INHERIT_PARTICIPANTS_FROM_PREVIOUS_ACTIVITY}
            legendItemDescriptionLocKey={LOC_LEGEND_INHERIT_PARTICIPANTS_DESCRIPTION} />
        <LegendItem
            legendItemIcon={<Icon iconCode={IconCode.uiPropertyAnd} />}
            legendItemHeaderLocKey={LOC_TYPE_FOR_JOIN}
            legendItemDescriptionLocKey={LOC_LEGEND_TYPE_FOR_JOIN_DESCRIPTION} />
        <LegendItem
            legendItemIcon={<Icon iconCode={IconCode.uiFullscreenA} color={IconColor.red} />}
            legendItemHeaderLocKey={LOC_ONLINE_EVAL_CONDITION}
            legendItemDescriptionLocKey={LOC_LEGEND_ONLINE_EVAL_CONDITION_DESCRIPTION} />
        <LegendItem
            legendItemIcon={<Icon iconCode={IconCode.uiFullscreenA} />}
            legendItemHeaderLocKey={LOC_BASIC_CONDITION}
            legendItemDescriptionLocKey={LOC_LEGEND_CONTAINS_BASIC_CONDITION_DESCRIPTION} />
        <LegendItem
            legendItemIcon={<svg width="30" height="10">
                <defs>
                    <marker id="markerArrow1" className={transitionClassNames("arrow-head")} markerWidth="13" markerHeight="13" refX="2" refY="6" orient="auto">
                        <path d="M2,2 L2,10 L10,6 L2,2" />
                    </marker>
                </defs>
                <line x1="0" y1="5" x2="23" y2="5" className={transitionClassNames({ "condition": true })} style={{ markerEnd: "url(#markerArrow1)" }} />
            </svg>}
            legendItemHeaderLocKey={LOC_BASIC_CONDITION}
            legendItemDescriptionLocKey={LOC_LEGEND_BASIC_CONDITION_DESCRIPTION} />
        <LegendItem
            legendItemIcon={<svg width="30" height="10">
                <defs>
                    <marker id="markerArrow2" className={transitionClassNames("arrow-head", { "has-actions": true })} markerWidth="13" markerHeight="13" refX="2" refY="6" orient="auto">
                        <path d="M2,2 L2,10 L10,6 L2,2" />
                    </marker>
                </defs>
                <line x1="0" y1="5" x2="23" y2="5" className={transitionClassNames({ "has-actions": true })} style={{ markerEnd: "url(#markerArrow2)" }} />
            </svg>}
            legendItemHeaderLocKey={LOC_CONTAINS_ACTIONS}
            legendItemDescriptionLocKey={LOC_LEGEND_CONTAINS_ACTIONS_DESCRIPTION} />
    </Collapsible>
);

type StateProps = Pick<LegendContainerProps, "isOpen" | "legendText">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    isOpen: getGeneralPanelStateContainer(state, hostKey, "LEGEND").isOpen,
    legendText: getPrimaryText(state, LOC_LEGEND) ?? "Legenda",
});

type DispatchProps = Pick<LegendContainerProps, "updateLegendPanelState">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, WorkflowEditorProps> = (dispatch, { hostKey }) => ({
    updateLegendPanelState: (isExpanded) => dispatch(createUpdateGeneralCollapsibleState(hostKey, "LEGEND", isExpanded)),
});

export const LegendContainer = connect(mapStateToProps, mapDispatchToProps)(LegendContainerComponent);
